// Shared helpers for the Ops desk module faces (OpsModulesRow1-3.tsx).
//
// Every query uses the SAME key, queryFn and refetchInterval as the panel that
// owns it (ruling 11), so a face and its drawer share one cache entry and a
// drawer open is a cache hit. react-query dedupes the many mounts of one key.
import { useQuery } from '@tanstack/react-query'
import {
  adaptiveApi,
  auditApi,
  controlsApi,
  engineApi,
  goalsApi,
  llmReasoningApi,
  mlApi,
  riskApi,
  signalsApi,
  systemApi,
} from '../../api/client'
import { RUNS_LIMIT } from './fleetModuleHooks'

/** The drawer keys of the Ops page, in on-screen order (spec §5.3). */
export const OPS_MODULE_KEYS = [
  'engine', 'runs-24h', 'services', 'cloudwatch', 'agents', 'market',
  'agent-health', 'signal-pipeline', 'sanity-gate', 'risk-monitor',
  'models', 'goals', 'operator',
] as const

export type OpsModuleKey = (typeof OPS_MODULE_KEYS)[number]

/** Grid column span of each module, index-aligned with OPS_MODULE_KEYS. */
export const OPS_SPANS = [2, 2, 2, 2, 2, 2, 7, 5, 3, 3, 2, 2, 2]

// ------------------------------------------------------------------- queries

/** Fleet EngineAlarmsModule's read (also useBooks'): status, run counters, books[]. */
export function useEngineStatus() {
  return useQuery({
    queryKey: ['engine-status'],
    queryFn: engineApi.getStatus,
    refetchInterval: 60000,
    retry: 1,
  })
}

/** WorkflowRunsFeed's page 1, sized like the Fleet pip strip (RUNS_LIMIT). */
export function useWorkflowRunsPage1() {
  return useQuery({
    queryKey: ['workflow-runs', 1],
    queryFn: () => auditApi.listRuns(RUNS_LIMIT, 1),
    refetchInterval: 10000,
  })
}

/** ServicesStatus's read. */
export function useServices() {
  return useQuery({
    queryKey: ['services-status'],
    queryFn: systemApi.getServices,
    refetchInterval: 15000,
  })
}

/** CloudWatchMetrics's read. */
export function useCloudWatch() {
  return useQuery({
    queryKey: ['cloudwatch-metrics'],
    queryFn: () => auditApi.cloudwatch(),
    refetchInterval: 60000,
  })
}

/** AgentHealthGrid's read of the runtime agents (`/system/agents`). */
export function useRuntimeAgents() {
  return useQuery({
    queryKey: ['agent-health'],
    queryFn: systemApi.getAgents,
    refetchInterval: 5000,
  })
}

/** VetoRateTrends's read: 7-day veto rates. */
export function useVetoRates() {
  return useQuery({
    queryKey: ['veto-rates'],
    queryFn: () => auditApi.vetoRates(7),
    refetchInterval: 60000,
  })
}

/** AppShell's read of system health (market status, pending signals, last update). */
export function useSystemHealth() {
  return useQuery({
    queryKey: ['system-health'],
    queryFn: () => systemApi.getHealth(),
    refetchInterval: 60000,
  })
}

/** AgentHealth24h's read: 24h decision totals per decision agent. */
export function useAgentHealth24h() {
  return useQuery({
    queryKey: ['agent-health-24h'],
    queryFn: auditApi.agentHealth,
    refetchInterval: 30000,
  })
}

/** SignalPipeline's read. */
export function useSignalPipeline() {
  return useQuery({
    queryKey: ['signal-pipeline'],
    queryFn: signalsApi.getPipeline,
    refetchInterval: 10000,
  })
}

/** RiskMonitor's read of the default book's risk metrics. */
export function useRiskMetrics() {
  return useQuery({
    queryKey: ['risk-metrics'],
    queryFn: riskApi.getMetrics,
    refetchInterval: 5000,
  })
}

/** RiskMonitor's read of the circuit breaker. */
export function useCircuitBreaker() {
  return useQuery({
    queryKey: ['circuit-breaker'],
    queryFn: riskApi.getCircuitBreaker,
    refetchInterval: 5000,
  })
}

/** MLEnsembleStatus's read. */
export function useEnsemble() {
  return useQuery({
    queryKey: ['ml-ensemble'],
    queryFn: mlApi.getEnsemble,
    refetchInterval: 30000,
  })
}

/** GoalsProgress's read. */
export function useGoals() {
  return useQuery({
    queryKey: ['goals'],
    queryFn: goalsApi.getAll,
    refetchInterval: 30000,
  })
}

/** AdaptiveTuningZone's read of the external-API pause switch. */
export function useControlsStatus() {
  return useQuery({
    queryKey: ['controls-status'],
    queryFn: () => controlsApi.getStatus(),
    refetchInterval: 30000,
  })
}

/** AdaptiveTuningZone's read of the adaptive tuner. */
export function useAdaptiveStatus() {
  return useQuery({
    queryKey: ['adaptive-status'],
    queryFn: adaptiveApi.status,
    refetchInterval: 60000,
  })
}

/** LLMCadenceToggle's read of the LLM reasoning envelope (cadence on/off). */
export function useLLMEnvelope() {
  return useQuery({
    queryKey: ['llm-reasoning-envelope'],
    queryFn: llmReasoningApi.envelope,
    refetchInterval: 60000,
  })
}
